import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../../Avatar.jsx';
import MenuItem from '../../Menu/MenuItem.jsx';
import MenuPanel from '../../Menu/MenuPanel.jsx';
import FilterSlideoutSection from '../FilterSlideoutSection.jsx';
import {
  clearSection,
  getSectionActiveCount,
  getVisibleItems,
  includesValue,
  joinClasses,
  toggleArrayValue,
} from '../filterSlideout.utils.js';

const ASSIGNMENT_AVATAR_PRESETS = [
  'border-[#80AFFF] bg-[#2173FF] text-white',
  'border-[#EDC6A6] bg-[#ED6C02] text-white',
  'border-[#80AFFF] bg-[#2173FF] text-white',
];

function resolveUserInitials(user) {
  if (typeof user.initials === 'string' && user.initials.trim()) {
    return user.initials.trim().slice(0, 2).toUpperCase();
  }

  const label = (user.label ?? '').trim();
  if (!label) return '?';

  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0][0] ?? '';
    const second = parts[1][0] ?? '';
    return `${first}${second}`.toUpperCase();
  }

  return label.slice(0, 2).toUpperCase();
}

function resolveUserAvatarClassName(user, index) {
  if (user.avatarClassName?.trim()) return user.avatarClassName.trim();
  if (user.isUnassigned) {
    return 'border-white bg-[#f2f2f2] text-[#666] dark:border-gray-950 dark:bg-gray-800 dark:text-gray-300';
  }
  return ASSIGNMENT_AVATAR_PRESETS[index % ASSIGNMENT_AVATAR_PRESETS.length];
}

function AssignmentUserAvatar({ user, index }) {
  const hasImage = typeof user.src === 'string' && user.src.trim().length > 0;
  const initials = resolveUserInitials(user);
  const avatarClassName = resolveUserAvatarClassName(user, index);

  return (
    <Avatar
      size="md"
      src={hasImage ? user.src : undefined}
      alt={user.label}
      initials={initials}
      className={joinClasses(
        hasImage ? '' : avatarClassName,
        '[&>span]:text-[10px] [&>span]:font-semibold [&>span]:leading-none',
        hasImage ? '' : '[&>span]:text-current',
      )}
    />
  );
}

function getPickerUsers(section) {
  const source = section.pickerUsers ?? section.users ?? [];
  const currentUser = source.find((user) => user.isCurrentUser);
  const unassigned = source.find((user) => user.isUnassigned);
  const rest = source.filter((user) => !user.isCurrentUser && !user.isUnassigned);
  return [currentUser, unassigned, ...rest].filter(Boolean);
}

function currentValueFromSection(section) {
  return Array.isArray(section.value) ? section.value : [];
}

function getAssignmentRowState(section, maxVisible) {
  const quickUsers = section.users ?? [];
  const selectedValues = new Set(Array.isArray(section.value) ? section.value : []);
  const selectedUsers = getPickerUsers(section).filter((user) => selectedValues.has(user.value));

  if (selectedUsers.length > 0) {
    const visibleSelected = selectedUsers.slice(0, maxVisible);
    const filler = quickUsers
      .filter((user) => !selectedValues.has(user.value))
      .slice(0, Math.max(maxVisible - visibleSelected.length, 0));

    return {
      visibleItems: [...visibleSelected, ...filler],
      hiddenCount: Math.max(selectedUsers.length - maxVisible, 0),
      hasOverflow: selectedUsers.length > maxVisible,
    };
  }

  return getVisibleItems(quickUsers, maxVisible, false);
}

function AssignmentMemberPicker({ section, anchorRef, panelRef }) {
  const users = useMemo(() => getPickerUsers(section), [section]);
  const valueRef = useRef(currentValueFromSection(section));
  valueRef.current = currentValueFromSection(section);
  const isDisabledRef = useRef(Boolean(section.disabled));
  isDisabledRef.current = Boolean(section.disabled);
  const onChangeRef = useRef(section.onChange);
  onChangeRef.current = section.onChange;

  const handleSelect = useCallback((user) => {
    if (user.disabled || isDisabledRef.current) return;
    onChangeRef.current?.(toggleArrayValue(valueRef.current, user.value));
  }, []);

  return (
    <MenuPanel
      ariaLabel={section.pickerAriaLabel ?? 'Select assignees'}
      className="z-[120] w-[260px]"
      maxHeightClass="max-h-[280px]"
      portal
      anchorRef={anchorRef}
      panelRef={panelRef}
      align="end"
    >
      {users.map((user, index) => {
        const selected = valueRef.current.includes(user.value);

        return (
          <MenuItem
            key={user.value}
            label={user.label}
            size="small"
            selected={selected}
            selectedSurface={false}
            disabled={user.disabled || section.disabled}
            checkbox={{ ariaLabel: `Select ${user.label}` }}
            onClick={() => handleSelect(user)}
          />
        );
      })}
    </MenuPanel>
  );
}

const AssignmentSection = ({ section }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRootRef = useRef(null);
  const pickerAnchorRef = useRef(null);
  const pickerPanelRef = useRef(null);
  const activeCount = getSectionActiveCount(section);
  const maxVisible = section.maxVisible ?? 3;
  const rowState = useMemo(
    () => getAssignmentRowState(section, maxVisible),
    [section, maxVisible],
  );
  const { visibleItems, hiddenCount, hasOverflow } = rowState;

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handlePointerDown = (event) => {
      if (pickerRootRef.current?.contains(event.target)) return;
      if (pickerPanelRef.current?.contains(event.target)) return;
      setPickerOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [pickerOpen]);

  const handleToggle = (user) => {
    if (user.disabled || section.disabled) return;
    section.onChange?.(toggleArrayValue(section.value, user.value));
  };

  const handlePickerToggle = () => {
    section.onAdd?.();
    setPickerOpen((value) => !value);
  };

  return (
    <FilterSlideoutSection
      title={section.title}
      defaultExpanded={section.defaultExpanded ?? true}
      disabled={section.disabled}
      activeCount={activeCount}
      onClear={() => clearSection(section)}
    >
      <div className="flex items-center gap-2">
        {visibleItems.map((user, index) => {
          const selected = includesValue(section.value, user.value);

          return (
            <button
              key={user.value}
              type="button"
              disabled={section.disabled || user.disabled}
              aria-pressed={selected}
              aria-label={user.label}
              title={user.label}
              className={joinClasses(
                'inline-flex shrink-0 items-center justify-center rounded-full transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2173FF]/60',
                selected
                  ? 'h-8 w-8 bg-white shadow-[0_0_10px_2px_rgba(33,115,255,0.45)] dark:bg-white dark:shadow-[0_0_10px_2px_rgba(59,130,246,0.6)]'
                  : 'h-6 w-6',
                user.disabled ? 'cursor-not-allowed opacity-50' : '',
              )}
              onClick={() => handleToggle(user)}
            >
              <AssignmentUserAvatar user={user} index={index} />
            </button>
          );
        })}

        <div ref={pickerRootRef} className="relative shrink-0">
          {hasOverflow ? (
            <button
              ref={pickerAnchorRef}
              type="button"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#f2f2f2] text-[10px] font-semibold leading-none text-[#666] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              onClick={handlePickerToggle}
              title={`${hiddenCount} more`}
              aria-haspopup="menu"
              aria-expanded={pickerOpen || undefined}
            >
              +{hiddenCount}
            </button>
          ) : (
            <button
              ref={pickerAnchorRef}
              type="button"
              className="h-7 shrink-0 rounded px-3 text-xs font-medium leading-5 text-[#2a2a2a] transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 dark:text-white"
              onClick={handlePickerToggle}
              aria-haspopup="menu"
              aria-expanded={pickerOpen || undefined}
            >
              {section.addLabel ?? '+ Add'}
            </button>
          )}
          {pickerOpen ? (
            <AssignmentMemberPicker
              section={section}
              anchorRef={pickerAnchorRef}
              panelRef={pickerPanelRef}
            />
          ) : null}
        </div>
      </div>
    </FilterSlideoutSection>
  );
};

export default memo(AssignmentSection);
